import {
  userSchema,
  userWithGoalsAndKpisSchema,
  type IUserServiceProvider,
  type UserType,
  type UserWithGoalsAndKpisType
} from '#domain/user';
import type { AvailableLanguagesType } from '#root/global.type';
import { useLanguage } from '#webui/components/composables/useLanguage';
import i18next from 'i18next';
import { defineStore } from 'pinia';
import { useInject } from '../core/utils/useInject';

export const useUserStore = defineStore('user', {
  state: () => ({
    me: null as UserType | null,
    loading: false,
    isAuthenticated: false,
    error: null as string | null,
    meWithGoalsAndKpis: null as UserWithGoalsAndKpisType | null
  }),
  actions: {
    async initializeLanguage() {
      const { setLanguage } = useLanguage();
      const detectedLanguage = localStorage.getItem('i18nextLng') || i18next.language;
      const language = detectedLanguage.split('-')[0] as AvailableLanguagesType;

      try {
        await setLanguage(language);
      } catch (error) {
        console.error('Failed to initialize language:', error);
      }
    },

    getMe: async function () {
      try {
        this.loading = true;
        const userServiceProvider = useInject<IUserServiceProvider>('userServiceProvider');
        await this.initializeLanguage();
        const userResponse = await userServiceProvider.getMe();
        this.me = userSchema.parse(userResponse);
        this.isAuthenticated = true;
      } catch (error) {
        this.error = (error as Error).message;
        this.me = null;
        this.isAuthenticated = false;
      } finally {
        this.loading = false;
      }
    },

    getUserWithGoalsAndKpis: async function (userServiceProvider: IUserServiceProvider) {
      try {
        const userResponse = await userServiceProvider.getUserWithGoalsAndKpis();
        this.meWithGoalsAndKpis = userWithGoalsAndKpisSchema.parse(userResponse);
      } catch (error) {
        this.error = (error as Error).message;
        this.meWithGoalsAndKpis = null;
      }
    }
  }
});
